/**
 * Gemeinsame Regeln fuer hochgeladene Dokumente (Hund und Wurf): PDF, XLSX und Bilder. Fotos kommen oft
 * als HEIC (iPhone) oder JFIF (unter Windows gespeicherte WhatsApp-/Web-Bilder) - JFIF ist technisch ein
 * JPEG, HEIC kann ausser Safari kein Browser anzeigen und der Server nicht lesen, deshalb wird es vor dem
 * Upload in JPEG umgewandelt.
 */
export const DOCUMENT_ACCEPT = [
  '.pdf', '.xlsx', '.jpg', '.jpeg', '.jfif', '.png', '.webp', '.heic', '.heif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'
].join(',');

const ALLOWED_DOCUMENT = /\.(pdf|xlsx|jpe?g|jfif|png|webp|heic|heif)$/i;
const HEIC = /\.(heic|heif)$/i;

export const isAllowedDocumentFile = (file: File) => ALLOWED_DOCUMENT.test(file.name);

/** Beim Hund zusaetzlich Embark-Dateien: Rohdaten (ZIP) fuer DNA-Profil und Inzucht, Ergebnisdatei (CSV) fuer Gesundheit und Merkmale. */
export const DOG_DOCUMENT_ACCEPT = [DOCUMENT_ACCEPT, '.zip', 'application/zip', 'application/x-zip-compressed', '.csv', 'text/csv'].join(',');

const ALLOWED_DOG_DOCUMENT = /\.(pdf|xlsx|jpe?g|jfif|png|webp|heic|heif|zip|csv)$/i;

export const isAllowedDogDocumentFile = (file: File) => ALLOWED_DOG_DOCUMENT.test(file.name);

/** Beim Interessenten zusaetzlich Word - Kaufvertraege liegen oft als DOCX vor. */
export const PROSPECT_DOCUMENT_ACCEPT = [
  DOCUMENT_ACCEPT, '.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
].join(',');

const ALLOWED_PROSPECT_DOCUMENT = /\.(pdf|docx|xlsx|jpe?g|jfif|png|webp|heic|heif)$/i;

export const isAllowedProspectDocumentFile = (file: File) => ALLOWED_PROSPECT_DOCUMENT.test(file.name);

/** Ahnentafel-Import beim Anlegen eines Hundes: nur was das Vision-Modell lesen kann, also PDF und Bilder. */
export const PEDIGREE_ACCEPT = [
  '.pdf', '.jpg', '.jpeg', '.jfif', '.png', '.webp', '.heic', '.heif',
  'application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'
].join(',');

const ALLOWED_PEDIGREE = /\.(pdf|jpe?g|jfif|png|webp|heic|heif)$/i;

export const isAllowedPedigreeFile = (file: File) => ALLOWED_PEDIGREE.test(file.name);

/** Wandelt HEIC/HEIF in JPEG um (Bibliothek wird erst bei Bedarf geladen); alle anderen Dateien bleiben unveraendert. */
export async function normalizeDocumentFile(file: File): Promise<File> {
  if (!HEIC.test(file.name) && !/^image\/hei[cf]$/i.test(file.type)) return file;

  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  return new File([blob], file.name.replace(HEIC, '.jpg'), { type: 'image/jpeg', lastModified: file.lastModified });
}

/** Support-Anhaenge: Screenshots, PDFs, Office- und Textdateien (z.B. Logs) - passt zu SupportController im Backend. */
export const SUPPORT_ATTACHMENT_ACCEPT = [
  DOCUMENT_ACCEPT, '.gif', 'image/gif', '.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.txt', '.log', 'text/plain'
].join(',');

const ALLOWED_SUPPORT_ATTACHMENT = /\.(pdf|docx|xlsx|jpe?g|jfif|png|webp|gif|heic|heif|txt|log)$/i;

export const isAllowedSupportAttachment = (file: File) => ALLOWED_SUPPORT_ATTACHMENT.test(file.name);
